// Organization Inquiry page — for organizational/cohort partners interested in bringing
// the AI Build Lab to their group. Submits to /api/org-inquiries.
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = [
  "County/Municipal Government", "University/Academic Institution", "Nonprofit",
  "Workforce Development Organization", "Small Business Program", "Accelerator/Incubator",
  "Community Organization", "Professional Association", "Ecosystem Partner",
  "Cohort Consultant/Independent", "Other",
];
const PARTICIPANT_RANGES = ["1–10", "11–25", "26–50", "51–100", "100+"];
const LOCATIONS = ["In-person", "Virtual", "Hybrid", "No preference"];
const INTEREST_OPTIONS = ["Private training", "Cohort training", "Sponsored community training", "Partnership"];

export default function OrganizationInquiry() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", organization_name: "", job_title: "", email: "", phone: "",
    org_type: "", audience_served: "", estimated_participants: "", preferred_timeframe: "",
    training_location: "", main_objective: "", interest_types: [], additional_comments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleInterest = (val) => {
    setForm((s) => ({
      ...s,
      interest_types: s.interest_types.includes(val) ? s.interest_types.filter((x) => x !== val) : [...s.interest_types, val],
    }));
  };

  const valid =
    form.first_name.trim() && form.last_name.trim() && form.organization_name.trim() &&
    form.job_title.trim() && /\S+@\S+\.\S+/.test(form.email) && form.org_type &&
    form.audience_served.trim() && form.estimated_participants && form.training_location &&
    form.main_objective.trim() && form.interest_types.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/org-inquiries`, form);
      setDone(true);
      toast.success("Inquiry sent. We'll follow up personally within 1–2 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-bone text-ink flex items-center justify-center px-6 py-20">
        <div className="max-w-xl text-center space-y-6">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-soft">
            <CheckCircle2 className="text-brand" size={32} />
          </div>
          <h1 className="font-display text-5xl tracking-[-0.025em] leading-tight">
            Inquiry <span className="font-serif-em italic text-brand font-light">received.</span>
          </h1>
          <p className="text-ink/70 text-[17px] leading-relaxed">
            Thanks, <strong>{form.first_name}</strong>. We&apos;ll reach out at <strong>{form.email}</strong> within 1&ndash;2 business days to schedule a conversation about bringing the AI Build Lab to <strong>{form.organization_name}</strong>.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-brand hover:underline" data-testid="org-back-home">
            <ArrowLeft size={14} /> Back to the AI Build Lab home
          </Link>
        </div>
      </div>
    );
  }

  const INPUT = "bg-white border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0";
  const LBL = "text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono";

  return (
    <div className="min-h-screen bg-bone text-ink">
      <header className="border-b border-[var(--line)] bg-bone/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-[-0.02em]">Freedom In Me <span className="text-brand">·</span> AI Build Lab</Link>
          <Link to="/" className="text-sm text-ink/65 hover:text-ink" data-testid="org-back-link">&larr; Back to home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <div className="mb-10">
          <div className="eyebrow mb-3">For organizations & cohort partners</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] mb-5">
            Bring the AI Build Lab <span className="font-serif-em italic text-brand font-light">to your organization.</span>
          </h1>
          <p className="text-ink/75 text-[17px] leading-relaxed max-w-2xl">
            For county and municipal teams, universities, nonprofits, workforce-development organizations, accelerators, and consultants who want to bring practical AI building to the people they serve. Tell us a little about your audience and we&apos;ll set up a short conversation.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="org-inquiry-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="of_fn" className={LBL}>First name *</Label><Input id="of_fn" data-testid="org-first-name" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className={INPUT} /></div>
            <div><Label htmlFor="of_ln" className={LBL}>Last name *</Label><Input id="of_ln" data-testid="org-last-name" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className={INPUT} /></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="of_org" className={LBL}>Organization name *</Label><Input id="of_org" data-testid="org-organization-name" value={form.organization_name} onChange={(e) => update("organization_name", e.target.value)} className={INPUT} /></div>
            <div><Label htmlFor="of_jt" className={LBL}>Job title *</Label><Input id="of_jt" data-testid="org-job-title" value={form.job_title} onChange={(e) => update("job_title", e.target.value)} className={INPUT} /></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="of_em" className={LBL}>Email *</Label><Input id="of_em" type="email" data-testid="org-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={INPUT} /></div>
            <div><Label htmlFor="of_ph" className={LBL}>Phone</Label><Input id="of_ph" type="tel" data-testid="org-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={INPUT} /></div>
          </div>

          <div>
            <Label className={LBL}>Type of organization *</Label>
            <Select value={form.org_type} onValueChange={(v) => update("org_type", v)}>
              <SelectTrigger data-testid="org-type" className={INPUT + " text-left"}><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{ORG_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div><Label htmlFor="of_aud" className={LBL}>Audience or cohort served *</Label><Input id="of_aud" data-testid="org-audience" value={form.audience_served} onChange={(e) => update("audience_served", e.target.value)} placeholder="e.g. minority business owners, faith leaders, workforce trainees" className={INPUT} /></div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className={LBL}>Estimated participants *</Label>
              <Select value={form.estimated_participants} onValueChange={(v) => update("estimated_participants", v)}>
                <SelectTrigger data-testid="org-participants" className={INPUT + " text-left"}><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>{PARTICIPANT_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="of_tf" className={LBL}>Preferred timeframe</Label><Input id="of_tf" data-testid="org-timeframe" value={form.preferred_timeframe} onChange={(e) => update("preferred_timeframe", e.target.value)} placeholder="e.g. Q3 2026, fall semester" className={INPUT} /></div>
          </div>

          <div>
            <Label className={LBL}>Training location *</Label>
            <Select value={form.training_location} onValueChange={(v) => update("training_location", v)}>
              <SelectTrigger data-testid="org-location" className={INPUT + " text-left"}><SelectValue placeholder="Select location preference" /></SelectTrigger>
              <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="of_obj" className={LBL}>Main training objective *</Label>
            <Textarea id="of_obj" data-testid="org-objective" value={form.main_objective} onChange={(e) => update("main_objective", e.target.value)} rows={3} placeholder="What outcome are you hoping your participants walk away with?" className="bg-white border border-[var(--line)] text-ink mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0" />
          </div>

          <div>
            <Label className={LBL}>Interest type * (select all that apply)</Label>
            <div className="mt-2 space-y-2">
              {INTEREST_OPTIONS.map((opt) => (
                <label key={opt} className="flex items-center gap-3 cursor-pointer" data-testid={`org-interest-${opt.toLowerCase().replace(/\W+/g, "-")}`}>
                  <Checkbox checked={form.interest_types.includes(opt)} onCheckedChange={() => toggleInterest(opt)} />
                  <span className="text-sm text-ink/85">{opt}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="of_cm" className={LBL}>Additional comments</Label>
            <Textarea id="of_cm" data-testid="org-comments" value={form.additional_comments} onChange={(e) => update("additional_comments", e.target.value)} rows={3} className="bg-white border border-[var(--line)] text-ink mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0" />
          </div>

          <div className="pt-4">
            <Button type="submit" disabled={submitting || !valid} data-testid="org-submit" className="btn-ink rounded-full h-12 px-7 text-[15px]">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : "Send inquiry"}
            </Button>
            <p className="text-xs text-ink/55 mt-3">We&apos;ll personally follow up within 1&ndash;2 business days.</p>
          </div>
        </form>
      </main>
    </div>
  );
}
